import UnsavedChangesBoundary from "./components/common/UnsavedChangesBoundary";
import React, { useEffect, useId, useRef, useState } from "react";
import RiskDetailTabs from "./RiskDetailTabs";
import AddChildRiskForm from "./AddChildRiskForm";
import Button from "./ui/Button";
import { typography } from "./ui/tokens";
import DrawerA11yWrapper from "./ui/DrawerA11yWrapper";
import useContentFontScale from "./hooks/useContentFontScale";

export default function RiskDetailDrawer({ riskId, onClose, onOpenInPanel, onRiskUpdated }) {
  const [showChildForm, setShowChildForm] = useState(false);
  const { scale: contentFontScale } = useContentFontScale();
  const titleId = useId();
  const closeRef = useRef(null);

  // Allow clicking breadcrumbs or child risks to open another risk.
  useEffect(() => {
    const handler = (e) => {
      const newRiskId = e.detail;

      // Close current drawer smoothly
      onClose();

      // Re-open drawer with new risk after a tiny delay
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("openRiskDrawer", { detail: newRiskId })
        );
      }, 10);
    };

    window.addEventListener("openRiskFromChild", handler);
    return () => window.removeEventListener("openRiskFromChild", handler);
  }, [onClose]);

  if (!riskId) return null;

  return (
    <UnsavedChangesBoundary onClose={onClose} itemName="risk">
      {(requestClose) => (
    <DrawerA11yWrapper
      open={Boolean(riskId)}
      onClose={requestClose}
      labelledBy={titleId}
      initialFocusRef={closeRef}
      backdropStyle={overlayStyle}
      containerStyle={drawerStyle}
    >
      <div
        style={{
          fontSize: "calc(1rem * var(--content-font-scale))",
          "--content-font-scale": String(contentFontScale),
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* HEADER */}
        <div style={headerStyle}>
          <div id={titleId} style={{ fontSize: typography.size.lg, fontWeight: typography.weight.medium }}>
            Risk Details
          </div>

          {/* BUTTONS */}
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Button
              onClick={() => setShowChildForm(true)}
              variant="primary"
              style={{
                background: "#0c1116",
                borderColor: "#0c1116",
                padding: "6px 12px",
                borderRadius: "6px",
                fontSize: typography.size.sm,
              }}
            >
              + Add Child Risk
            </Button>

            <Button
              onClick={requestClose}
              variant="ghost"
              style={closeButtonStyle}
              aria-label="Close risk drawer"
              ref={closeRef}
            >
              ?
            </Button>
          </div>
        </div>

        {/* CONTENT */}
        <div style={contentStyle}>
          <RiskDetailTabs
            riskId={riskId}
            onRiskUpdated={onRiskUpdated}
            // When a risk name is clicked in the drawer, promote it to the main panel if provided.
            onOpenInPanel={(id) => {
              if (onClose) onClose();
              if (onOpenInPanel) onOpenInPanel(id);
            }}
          />
        </div>
      </div>

      {/* CHILD RISK MODAL */}
      {showChildForm && (
        <AddChildRiskForm
          parentId={riskId}
          onClose={() => setShowChildForm(false)}
          onSaved={() => {
            setShowChildForm(false);

            // Auto-refresh drawer after saving a child risk
            window.dispatchEvent(
              new CustomEvent("openRiskFromChild", {
                detail: riskId,
              })
            );
          }}
        />
      )}
    </DrawerA11yWrapper>
      )}
    </UnsavedChangesBoundary>
  );
}

/* ---------------- STYLES ---------------- */

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "stretch",
  zIndex: 3000,
  animation: "fadeIn 0.2s ease-out",
};

const drawerStyle = {
  width: "500px",
  maxWidth: "90vw",
  height: "100%",
  background: "#ffffff",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 0 20px rgba(0,0,0,0.15)",
  animation: "slideIn 0.25s ease-out",
};

const headerStyle = {
  padding: "16px 20px",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#fff",
  position: "sticky",
  top: 0,
  zIndex: 10,
};

const closeButtonStyle = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontSize: typography.size.xl,
  color: "#6b7280",
  padding: "4px 8px",
};

const contentStyle = {
  padding: "20px",
  overflowY: "auto",
  flex: 1,
};

/* CSS animations injected globally */
const styleTag = document.createElement("style");
styleTag.innerHTML = `
@keyframes slideIn {
  from { transform: translateX(40px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
`;
document.head.appendChild(styleTag);
